import * as React from "react";
import { Carousel } from "@scandotcom/react";
import RestClient from "../lib/RestClient";
import {
  EventbriteEvent,
  EventbriteResponse as EventbriteEventsResponse,
} from "../types/eventbrite/event";
import * as E from "fp-ts/Either";
import * as A from "fp-ts/Array";
import { IconCalendarTime, IconMapPin } from "@tabler/icons-react";
import { isNotNil } from "../utils";

interface Props {}

export const EventsCarousel: React.FC<Props> = () => {
  const [events, setEvents] = React.useState<EventbriteEvent[] | null>(null);

  const fetchEvents = async () => {
    const response = await RestClient.get("/api/v1/events")
      .then((res) => res.data)
      .then(EventbriteEventsResponse.decode);

    if (E.isRight(response)) {
      setEvents(response.right.events);
    }
  };

  React.useEffect(() => {
    fetchEvents();
  }, []);

  if (!events || A.isEmpty(events)) {
    return null;
  }

  return (
    <section className="overflow-x-hidden">
      <div className="container">
        <Carousel
          headingContent={(buttons) => (
            <div className="mb-6 flex gap-4">
              <h2 className="display-3xl">Events</h2> {buttons}
            </div>
          )}
          items={events.map((event) => {
            const eventDateFormatter = new Intl.DateTimeFormat(undefined, {
              weekday: "long",
              day: "numeric",
              month: "long",
              timeZone: event.start.timezone,
            });
            const eventTimeFormatter = new Intl.DateTimeFormat(undefined, {
              hour: "numeric",
              minute: "numeric",
              timeZone: event.start.timezone,
            });

            return (
              <div key={event.id} className="flex h-full flex-col">
                <div className="bg-primary-200 shrink-0 overflow-hidden rounded-t-2xl">
                  {event.logo ? (
                    <img
                      src={event.logo.original.url}
                      className="h-auto w-full "
                      alt={`Banner for ${event.name.text}`}
                    />
                  ) : null}
                </div>
                <div className="flex-grow  rounded-b-2xl border border-t-0 border-neutral-200 p-6">
                  <h3 className="display-xl">{event.name.text}</h3>
                  <div className="mt-2 flex items-center gap-2">
                    <IconCalendarTime className="h5 w-5 shrink-0" />
                    <time
                      dateTime={event.start.utc}
                      className="flex gap-1 text-sm"
                    >
                      <span className="text-neutral-600">
                        {eventDateFormatter.format(new Date(event.start.utc))}
                      </span>{" "}
                      <span>&bull;</span>
                      <span>
                        {eventTimeFormatter.format(new Date(event.start.utc))}{" "}
                        Start
                      </span>
                    </time>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <IconMapPin className="h5 w-5 shrink-0" />
                    <address className="text-sm not-italic text-neutral-600">
                      {[
                        event.venue.name,
                        event.venue.address?.localized_address_display,
                      ]
                        .filter(isNotNil)
                        .join(", ")}
                    </address>
                  </div>

                  <div className="mt-4">
                    <a
                      href={event.url}
                      target="_blank"
                      className="button button-primary inline-block"
                    >
                      Book now
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        />
      </div>
    </section>
  );
};
